import type { EnMode } from '../components/watch/Transcript';
import type { ProfileUpdate } from '@nelnela/contracts';

/**
 * The write shapes for the three profile settings on Me, and the small rules around them.
 *
 * They are pure functions rather than inline object literals at the call sites because the
 * server MERGES `display` one key deep: a write that accidentally carried a second key would
 * silently overwrite a setting the learner did not touch. One function per setting means one
 * key per write, provably.
 */

export const TRANSCRIPT_SIZE_MIN = 16;
export const TRANSCRIPT_SIZE_MAX = 18;
/** `READING_SURFACE_CSS` falls back to this when `--nn-transcript-size` is unset. */
export const TRANSCRIPT_SIZE_DEFAULT = 17;

/** The stepper's bounds are also the contract's (`z.number().int().min(16).max(18)`). */
export function clampTranscriptSize(px: number): number {
  return Math.min(TRANSCRIPT_SIZE_MAX, Math.max(TRANSCRIPT_SIZE_MIN, Math.round(px)));
}

export function enVisibilityWrite(mode: EnMode): ProfileUpdate {
  return { display: { enVisibility: mode } };
}

export function keepPlayingWrite(on: boolean): ProfileUpdate {
  return { display: { keepPlayingWhileGlossing: on } };
}

export function transcriptSizeWrite(px: number): ProfileUpdate {
  return { display: { transcriptSize: clampTranscriptSize(px) } };
}

/**
 * What the App card says about storage. `persist()` returning false is reported honestly —
 * "best-effort" is true and useful, where hiding it would make an eviction inexplicable.
 */
export function storageLine(persisted: boolean | null): string {
  if (persisted === null) return 'not requested yet';
  return persisted ? 'persistent' : 'best-effort';
}

/** The three EN modes as the Watch strip labels them (`Watch.tsx` EN_LABEL). */
export const EN_OPTIONS: Array<{ value: EnMode; label: string }> = [
  { value: 'show', label: 'show' },
  { value: 'reveal', label: 'tap to reveal' },
  { value: 'hide', label: 'hide' },
];
